import React, { useState } from 'react';
import { Share2, Copy, X } from 'lucide-react';
import { trackShare } from '../utils/analytics';

interface ShareButtonProps {
  title: string;
  text: string;
  url?: string;
}

const ShareButton: React.FC<ShareButtonProps> = ({ title, text, url = window.location.href }) => {
  const [showFallback, setShowFallback] = useState(false);

  const showToast = (message: string) => {
    const toast = document.createElement('div');
    toast.className = 'fixed bottom-4 right-4 bg-green-500 text-white px-4 py-2 rounded-lg shadow-lg z-50';
    toast.textContent = message;
    document.body.appendChild(toast);
    
    setTimeout(() => {
      toast.remove();
    }, 3000);
  };

  const handleShare = async () => {
    try {
      // Check if Web Share API is available and supported
      if (navigator.share && navigator.canShare && navigator.canShare({ title, text, url })) {
        await navigator.share({
          title,
          text,
          url
        });
        trackShare('Web Share API', text, 'native');
      } else {
        // Show fallback UI if Web Share API is not available
        setShowFallback(true);
      }
    } catch (error) {
      // Handle permission denied or other errors by showing fallback UI
      console.error('Share failed:', error);
      setShowFallback(true);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(`${text}\n\nCheck it out at: ${url}`);
      trackShare('Copy Link', text, 'clipboard');
      showToast('Link copied to clipboard!');
      setShowFallback(false);
    } catch (error) {
      console.error('Copy failed:', error);
      // If clipboard API fails, show the text in a selectable input
      setShowFallback(true);
    }
  };

  return (
    <>
      <button
        onClick={handleShare}
        className="text-gray-400 hover:text-white transition flex items-center space-x-1 px-3 py-1.5 rounded-full hover:bg-white/10"
      >
        <Share2 className="h-4 w-4" />
        <span className="text-sm">Share</span>
      </button>

      {/* Fallback UI */}
      {showFallback && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-900 rounded-lg p-6 max-w-md w-full relative">
            <button
              onClick={() => setShowFallback(false)}
              className="absolute top-2 right-2 text-gray-400 hover:text-white p-2"
            >
              <X className="h-5 w-5" />
            </button>

            <h3 className="text-lg font-semibold text-white mb-4">Share this content</h3>
            
            <div className="space-y-4">
              <div className="bg-black/50 rounded-lg p-4">
                <p className="text-gray-300 text-sm">{text}</p>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={url}
                  readOnly
                  className="flex-1 bg-black/50 border border-white/10 rounded-lg px-3 py-2 text-sm text-gray-300"
                />
                <button
                  onClick={handleCopy}
                  className="gradient-bg text-white px-4 py-2 rounded-lg hover:opacity-90 transition flex items-center space-x-2"
                >
                  <Copy className="h-4 w-4" />
                  <span>Copy</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ShareButton;